'use client'

import { useEffect } from 'react'

import { clientEnvs } from '@/config'
import { logger } from '@/lib/observability/logger'

import { useAnalyticsConsentContext } from './AnalyticsConsentProvider'

type Spindl = typeof import('@spindl-xyz/attribution-lite').default

let spindl: Promise<Spindl | undefined> | undefined

// Spindl reads browser storage while its module initializes, which throws when
// storage access is blocked (privacy settings, sandboxed iframes such as Safe
// Apps). Loading it on demand turns that failure into "no attribution".
export const loadSpindl = () =>
  (spindl ??= import('@spindl-xyz/attribution-lite').then(
    ({ default: sdk }) => {
      if (clientEnvs.NEXT_PUBLIC_SPINDL_SDK_KEY)
        sdk.configure({
          sdkKey: clientEnvs.NEXT_PUBLIC_SPINDL_SDK_KEY,
          debugMode: process.env.NODE_ENV !== 'production'
          // TODO: support rewrites for this.
          // host: `${window.location.origin}/ingest`,
        })
      return sdk
    },
    (error: unknown) => {
      spindl = undefined
      logger.warn('Spindl failed to load; attribution disabled', { extra: { error } })
      return undefined
    }
  ))

// Spindl follows the same "Share analytics" preference as PostHog: nothing is
// sent (`enableAutoPageViews`, `attribute`) while the user is opted out, and the
// auto page-view listener is torn down as soon as they opt out. `loadSpindl`
// may configure the SDK after an opt-out, but that only stores config in memory. The SDK only
// arms auto page views once per page load, so re-enabling resumes them on the
// next load.
export const SpindlProvider = () => {
  const { isAnalyticsEnabled } = useAnalyticsConsentContext()

  useEffect(() => {
    if (!clientEnvs.NEXT_PUBLIC_SPINDL_SDK_KEY || !isAnalyticsEnabled) return

    let isActive = true
    let disposeAutoPageViews: (() => void) | undefined

    const start = async () => {
      const sdk = await loadSpindl()
      if (!sdk || !isActive) return

      disposeAutoPageViews = sdk.enableAutoPageViews()
    }
    void start()

    return () => {
      isActive = false
      disposeAutoPageViews?.()
    }
  }, [isAnalyticsEnabled])

  return null
}
